import { Components, Theme } from '@mui/material'

const loadingIndicatorSelector = '&.MuiButton-loadingPositionCenter .MuiButton-loadingIndicator'

// TODO - when we've removed all usage of outlined variant, we can put this in to stop it coming back
// declare module '@mui/material/Button' {
//   interface ButtonPropsVariantOverrides {
//     outlined: false
//   }
// }

export const primaryContainedButtonStyle = (theme: Theme) => ({
  backgroundColor: theme.palette.futureTheme.bg.brand.default,
  color: theme.palette.futureTheme.text.brand.onBrand,
  '&:hover': {
    backgroundColor: theme.palette.futureTheme.bg.brand.hover,
  },
  [`&.Mui-disabled, ${loadingIndicatorSelector}`]: {
    backgroundColor: theme.palette.futureTheme.bg.brand.default,
    color: theme.palette.futureTheme.text.brand.onBrand,
  },
})

export const secondaryContainedButtonStyle = (theme: Theme) => ({
  backgroundColor: theme.palette.futureTheme.bg.surface.elevated,
  borderStyle: 'solid',
  borderWidth: theme.futureTheme.strokeWidth.default,
  borderColor: theme.palette.futureTheme.border.subtle,
  color: theme.palette.futureTheme.text.muted,
  boxShadow: theme.futureTheme.shadows.buttonDrop,
  '&:hover': {
    backgroundColor: theme.palette.futureTheme.bg.surface.elevated,
    color: theme.palette.futureTheme.text.default,
    borderColor: theme.palette.futureTheme.border.strong,
    boxShadow: [theme.futureTheme.shadows.buttonInner, theme.futureTheme.shadows.buttonDrop].join(', '),
  },
  [`&.Mui-disabled, ${loadingIndicatorSelector}`]: {
    backgroundColor: theme.palette.futureTheme.bg.surface.elevated,
    color: theme.palette.futureTheme.text.default,
    boxShadow: [theme.futureTheme.shadows.buttonInner, theme.futureTheme.shadows.buttonDrop].join(', '),
  },
})

export const MuiButton: Components<Omit<Theme, 'components'>>['MuiButton'] = {
  defaultProps: {
    disableRipple: true,
    disableElevation: true,
    loadingPosition: 'center',
  },
  styleOverrides: {
    root: ({ theme }) => ({
      '&.Mui-disabled': {
        opacity: 0.4,
      },
      '&.Mui-disabled.MuiButton-loading.MuiButton-loadingPositionCenter': {
        color: 'transparent',
      },
      ...theme.typography['futureTheme.body-medium'],
      textTransform: 'none',
    }),
    startIcon: ({ theme }) => ({
      marginRight: theme.spacing(1),
      marginLeft: theme.spacing(-1),
      '& > *:nth-of-type(1)': {
        fontSize: 18,
      },
    }),
    endIcon: ({ theme }) => ({
      marginLeft: theme.spacing(1),
      marginRight: theme.spacing(-1),
      '& > *:nth-of-type(1)': {
        fontSize: 18,
      },
    }),
  },
  variants: [
    {
      props: { variant: 'text' },
      style: ({ theme }) => ({
        backgroundColor: theme.palette.futureTheme.bg.invisible,
      }),
    },
    // Colour
    {
      props: { color: 'primary', variant: 'contained' },
      style: ({ theme }) => primaryContainedButtonStyle(theme),
    },
    {
      props: { color: 'primary', variant: 'text' },
      style: ({ theme }) => ({
        color: theme.palette.futureTheme.text.brand.default,
        '&:hover': {
          backgroundColor: theme.palette.futureTheme.bg.brand.muted,
          color: theme.palette.futureTheme.text.brand.onBrandMuted,
        },
        [`&.Mui-disabled, ${loadingIndicatorSelector}`]: {
          color: theme.palette.futureTheme.text.brand.default,
        },
      }),
    },
    {
      // Transition - all outlined buttons render as secondary contained
      // Can simplify this when we remove outlined variant usage
      props: (props) => (props.color === 'secondary' && props.variant === 'contained') || props.variant === 'outlined',
      style: ({ theme }) => secondaryContainedButtonStyle(theme),
    },
    {
      props: { color: 'secondary', variant: 'text' },
      style: ({ theme }) => ({
        color: theme.palette.futureTheme.text.muted,
        '&:hover': {
          color: theme.palette.futureTheme.text.default,
          backgroundColor: theme.palette.futureTheme.bg.interactive.hover,
        },
        [`&.Mui-disabled, ${loadingIndicatorSelector}`]: {
          color: theme.palette.futureTheme.text.default,
        },
      }),
    },
    {
      props: { color: 'error', variant: 'contained' },
      style: ({ theme }) => ({
        backgroundColor: theme.palette.futureTheme.bg.danger.default,
        color: theme.palette.futureTheme.text.danger.onDanger,
        '&:hover': {
          backgroundColor: theme.palette.futureTheme.bg.danger.hover,
        },
        [`&.Mui-disabled, ${loadingIndicatorSelector}`]: {
          backgroundColor: theme.palette.futureTheme.bg.danger.default,
          color: theme.palette.futureTheme.text.danger.onDanger,
        },
      }),
    },
    {
      props: { color: 'error', variant: 'text' },
      style: ({ theme }) => ({
        color: theme.palette.futureTheme.text.danger.default,
        '&:hover': {
          color: theme.palette.futureTheme.text.danger.onDangerMuted,
          backgroundColor: theme.palette.futureTheme.bg.danger.muted,
        },
        [`&.Mui-disabled, ${loadingIndicatorSelector}`]: {
          color: theme.palette.futureTheme.text.danger.onDangerMuted,
        },
      }),
    },
    // Size
    {
      props: { size: 'large' },
      style: ({ theme }) => ({
        borderRadius: theme.shape['futureTheme.radius-lg'],
        padding: theme.spacing(2, 4),
      }),
    },
    {
      props: { size: 'medium' },
      style: ({ theme }) => ({
        borderRadius: theme.shape['futureTheme.radius-lg'],
        padding: theme.spacing(1.5, 3),
      }),
    },
    {
      props: { size: 'small' },
      style: ({ theme }) => ({
        borderRadius: theme.shape['futureTheme.radius-md'],
        padding: theme.spacing(0.5, 1.5),
      }),
    },
  ],
}
